"use client";

import * as React from "react";
import { Form as FormPrimitive } from "@base-ui/react/form";
import { Loader2 } from "lucide-react";
import type { output, ZodType } from "zod";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/apiCall";
import { isRecord } from "@/lib/isRecord";
import { isNotSignedIn } from "@/lib/session/sessionEnded";
import { cn } from "@/lib/utils";

/** Problems by field name, as Base UI's Form takes them. */
export type FieldErrors = Record<string, string[]>;

/**
 * The zod path of a form: the values checked against one schema, and each problem filed under the field it belongs to
 * (the issue's path, joined with dots, is the field's `name`). A problem with the whole form files under "".
 */
export function checkWithSchema<S extends ZodType>(
  schema: S,
  values: unknown
): { ok: true; data: output<S> } | { ok: false; errors: FieldErrors } {
  const result = schema.safeParse(values);
  if (result.success) return { ok: true, data: result.data };
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) (errors[issue.path.join(".")] ??= []).push(issue.message);
  return { ok: false, errors };
}

/**
 * The problems a refusal names field by field: the API's problem details carry `errors[]`, each with a JSON pointer into
 * the body ("#/body/email") and its message. Only pointers into the body name a field; the rest is the form's.
 */
export function refusedFields(e: ApiError): FieldErrors {
  const listed = isRecord(e.body) && Array.isArray(e.body.errors) ? e.body.errors : [];
  const errors: FieldErrors = {};
  for (const item of listed) {
    if (!isRecord(item) || typeof item.pointer !== "string" || typeof item.message !== "string") continue;
    const field = /^#\/body\/(.+)$/.exec(item.pointer)?.[1]?.split("/").join(".");
    if (field) (errors[field] ??= []).push(item.message);
  }
  return errors;
}

/** The first problem a schema finds with `values`, or null when they pass: for a screen whose Save is not a form's (the Capture card). */
export function formProblem<S extends ZodType>(schema: S, values: unknown): string | null {
  const checked = checkWithSchema(schema, values);
  return checked.ok ? null : (Object.values(checked.errors)[0]?.[0] ?? null);
}

/** What a refusal lists beyond its sentence (a 409 naming each thing still needed): what did not happen, and each reason. */
export type RefusalDetail = { heading: string; items: readonly string[] };

/**
 * A save refused, said by the screen itself: reject `onValid` with it when the answer is not an `ApiError` (a sign-in that did
 * not match, say). Each reason in `fields` shows on its field, as a schema's does; `said` is a sentence for the form as a whole,
 * drawn where the screen puts `useFormRefusal()` (beside `FormSubmit` unless it is `bare`). With a `detail` the refusal is a
 * warning of its own, drawn where the screen puts `FormRefusalNotice`.
 */
export class FormRefusal extends Error {
  constructor(
    readonly fields: FieldErrors,
    readonly said: string | null = null,
    readonly detail: RefusalDetail | null = null
  ) {
    super("refused");
    this.name = "FormRefusal";
  }
}

type FormState = { pending: boolean; refusal: string | null; detail: RefusalDetail | null; attempted: boolean };
const IDLE: FormState = { pending: false, refusal: null, detail: null, attempted: false };
const FormContext = React.createContext<FormState & { ready: boolean }>({ ...IDLE, ready: true });

type FormProps<S extends ZodType> = Omit<FormPrimitive.Props, "onFormSubmit" | "errors" | "onSubmit" | "onReset"> & {
  /** The form's one schema. On submit the values are checked against it; each problem shows on its field. */
  schema: S;
  /**
   * The save, with the checked values. While it runs the form is locked (FormSubmit spins, a second submit does nothing).
   * If it rejects with the route's ApiError, the problems it names by field show on those fields and the rest is the
   * form's refusal, said beside FormSubmit. Send the write `inline` so no toast says it twice.
   */
  onValid: (values: output<S>) => void | Promise<unknown>;
  /**
   * Values the form holds outside its fields (a signature pad, a grid of days), joined with the fields' own before the
   * schema checks them. A problem filed under such a name shows on a `Field` of that name.
   */
  values?: Record<string, unknown>;
  /**
   * The submit button stays locked until `values` pass the schema: for a form whose values are all held outside its fields
   * (a dialog that states a decision), where the person is told what is missing by a button that will not press.
   */
  whenValid?: boolean;
  /** What the form saves, as a sentence opens with it ("The assessor"): the default refusal. */
  name?: string;
  /** The screen's own sentence for a refusal; by default "<name> could not be saved, so nothing has changed. Try again." */
  refused?: (e: ApiError) => string;
};

/**
 * A form: Base UI's Form with one zod schema. The browser's own checks on a control (`required`, `type="email"`) run first,
 * then the schema, then the save; focus moves to the first field with a problem, and a field's problem clears when its
 * value changes. A reset (an edit thrown away) clears its problems and its refusal. A form drawn inside this one's tree in a
 * portal (a dialog's) is its own: its submit and reset never reach this one.
 */
function Form<S extends ZodType>({
  schema,
  onValid,
  values: held,
  whenValid = false,
  name = "This",
  refused,
  className,
  children,
  ...props
}: FormProps<S>) {
  const [errors, setErrors] = React.useState<FieldErrors>({});
  const [state, setState] = React.useState<FormState>(IDLE);
  const running = React.useRef(false);
  // React carries an event up its own tree, through a portal: a submit that bubbled here from a form inside it is not this form's.
  const foreign = React.useRef(false);

  async function submit(values: Record<string, unknown>) {
    if (running.current) return;
    const checked = checkWithSchema(schema, { ...values, ...held });
    setErrors(checked.ok ? {} : checked.errors);
    if (!checked.ok) {
      setState((s) => ({ ...s, attempted: true }));
      return;
    }
    running.current = true;
    setState({ ...IDLE, pending: true, attempted: true });
    try {
      await onValid(checked.data);
      setState({ ...IDLE, attempted: true });
    } catch (e) {
      if (e instanceof FormRefusal) {
        setErrors(e.fields);
        setState({ ...IDLE, refusal: e.said, detail: e.detail, attempted: true });
        return;
      }
      const refusal = e instanceof ApiError && !isNotSignedIn(e.status, e.body) ? e : null;
      const byField = refusal ? refusedFields(refusal) : {};
      setErrors(byField);
      setState({
        ...IDLE,
        refusal:
          refusal && !Object.keys(byField).length
            ? (refused?.(refusal) ?? `${name} could not be saved, so nothing has changed. Try again.`)
            : null,
        attempted: true,
      });
      // Anything but a refusal is a fault, not an answer: it is thrown on, never worded as a refusal.
      if (!(e instanceof ApiError)) throw e;
    } finally {
      running.current = false;
    }
  }

  const ready = !whenValid || checkWithSchema(schema, { ...held }).ok;
  return (
    <FormContext.Provider value={{ ...state, ready }}>
      <FormPrimitive
        data-slot="form"
        aria-busy={state.pending || undefined}
        className={cn("grid gap-4", className)}
        errors={errors}
        onSubmit={(event) => {
          foreign.current = event.target !== event.currentTarget;
          if (!foreign.current) event.stopPropagation();
        }}
        onFormSubmit={(values) => {
          if (!foreign.current) void submit(values);
        }}
        onReset={(event) => {
          if (event.target !== event.currentTarget) return;
          event.stopPropagation();
          setErrors({});
          setState((s) => ({ ...IDLE, pending: s.pending }));
        }}
        {...props}
      >
        {children}
      </FormPrimitive>
    </FormContext.Provider>
  );
}

/**
 * The form's submit button: spinning with `pending` words while the save runs, locked until it answers. A refusal that names
 * no field is said beside it, as an action that did not go through is everywhere (one with a detail is `FormRefusalNotice`'s).
 */
function FormSubmit({
  children,
  pending,
  variant,
  size,
  fill = false,
  bare = false,
  busy = false,
  disabled = false,
  className,
}: {
  children: React.ReactNode;
  /** The button's words while the save runs ("Saving…"). */
  pending: React.ReactNode;
  variant?: React.ComponentProps<typeof Button>["variant"];
  size?: React.ComponentProps<typeof Button>["size"];
  /** The button takes the form's whole width, with a refusal under it (the sign-in pages). */
  fill?: boolean;
  /** Just the button (taking `className`), for a footer that lays its buttons out itself; a refusal is then said by the screen. */
  bare?: boolean;
  /** The screen's own write is out as well (a change its parent is saving): the button reads as saving too. */
  busy?: boolean;
  /** The form cannot be sent yet, for a reason the screen says elsewhere. */
  disabled?: boolean;
  className?: string;
}) {
  const form = React.useContext(FormContext);
  const saving = form.pending || busy;
  const button = (
    <Button
      type="submit"
      variant={variant}
      size={size}
      className={bare ? className : undefined}
      disabled={saving || disabled || !form.ready}
    >
      {saving ? (
        <>
          <Loader2 data-icon="inline-start" className="animate-spin" aria-hidden /> {pending}
        </>
      ) : (
        children
      )}
    </Button>
  );
  if (bare) return button;
  return (
    <div
      data-slot="form-submit"
      className={cn("flex flex-wrap items-center gap-2", fill && "flex-col items-stretch", className)}
    >
      {button}
      {form.refusal && !form.detail ? (
        <p role="alert" className="text-small text-destructive">
          {form.refusal}
        </p>
      ) : null}
    </div>
  );
}

/** The form's refusal where the screen puts it: with a detail, a warning headed by it, the sentence, each reason; else a line. */
function FormRefusalNotice({ className }: { className?: string }) {
  const { refusal, detail } = React.useContext(FormContext);
  if (detail)
    return (
      <Alert variant="warning" className={className}>
        <AlertTitle>{detail.heading}</AlertTitle>
        <AlertDescription>
          {refusal}
          {detail.items.length ? (
            <ul className="mt-1 list-disc space-y-0.5 pl-4">
              {detail.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ) : null}
        </AlertDescription>
      </Alert>
    );
  return refusal ? (
    <p role="alert" className={cn("text-small text-destructive", className)}>
      {refusal}
    </p>
  ) : null;
}

/** The form's refusal, as a sentence: for a screen that says it somewhere other than beside `FormSubmit` (a dialog's alert). */
function useFormRefusal(): string | null {
  return React.useContext(FormContext).refusal;
}

/** Whether the form's save is out: for a submit button of its own that FormSubmit cannot draw (the sign-in button's sweep). */
function useFormPending(): boolean {
  return React.useContext(FormContext).pending;
}

/** Whether a send was tried at least once, passing the schema or not: for a screen that marks what is missing only after the person asked to save. */
function useFormAttempted(): boolean {
  return React.useContext(FormContext).attempted;
}

export { Form, FormRefusalNotice, FormSubmit, useFormAttempted, useFormPending, useFormRefusal };
